import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionAPI } from '../services/api';
import Logo from '../components/Logo';
import { trackSignupConversion } from '../services/analytics';
import './Register.css';

/**
 * /start — the package-first entry path. Slice 4.
 *
 * Lives BESIDE /register, which is untouched. Nothing points here yet; it is
 * reachable by URL only until the switch is flipped at slice 6. That is what
 * makes rewriting the signup path safe on a product with 26,234 live students:
 * the new road is built next to the old one and traffic moves in one edit.
 *
 * The difference from /register is one question, and it is the whole point:
 *
 *   /register  name · school · STREAM  ->  free tier  ->  dashboard
 *   /start     name · school           ->  free tier  ->  PACKAGES
 *
 * Stream is gone from signup because it is a property of the ESSLCE program,
 * not of a person. UAT is stream-independent; an exit exam wants a department
 * instead. Asking every student to classify themselves before they have said
 * what they are preparing for is asking the wrong question in the wrong order,
 * and it is the reason a second exam could not previously be added without
 * touching registration.
 *
 * The picker asks whatever the chosen program declares, and
 * `_bootstrap_student_row` writes stream and subjects onto the student row at
 * that moment — so a student arriving through here reaches a dashboard in
 * exactly the same shape as one who came through /register.
 */

const Start: React.FC = () => {
  const { student, register } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ first_name: '', school_name: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await register({
        ...formData,
        last_name: '',
        profile_name: formData.first_name,
        grade_level: 12,
        // No stream. The API accepts its absence since slice 4; the package
        // supplies it.
        phone_number: student?.phone_number || '',
      });
      trackSignupConversion();

      // Keep the free-tier assignment the old path did. Tier and package stay
      // orthogonal — a tier is a recurring plan on a person, a package is a
      // one-time purchase of a program — so this is not the thing being
      // replaced, and skipping it would leave the student with no subscription
      // row at all.
      try {
        await subscriptionAPI.assignFreeTier();
      } catch {
        // Already has one, or the tier service is unhappy. Neither should
        // block a student from choosing what to study.
      }

      navigate('/packages');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="register-header">
          <div className="register-logo">
            <Logo height={52} />
          </div>
          <h1>Let&rsquo;s get you started</h1>
          <p className="subtitle">
            Two details, then you pick what you&rsquo;re preparing for.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="first_name">First Name</label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="Your first name"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="school_name">
              School Name <span className="label-optional">(optional)</span>
            </label>
            <input
              type="text"
              id="school_name"
              name="school_name"
              value={formData.school_name}
              onChange={handleChange}
              placeholder="e.g., Medhane Alem School"
              disabled={loading}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            className="submit-button"
            disabled={loading || !formData.first_name.trim()}
          >
            {loading ? 'Setting up...' : 'Next: choose your exam'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Start;
